'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ProgressBar from '@/components/tools/ProgressBar';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CityData {
  overall: number;
  housing: number;
  food: number;
  transportation: number;
  healthcare: number;
  utilities: number;
  entertainment: number;
}

type Category = keyof Omit<CityData, 'overall'>;

const CATEGORIES: { key: Category; label: string }[] = [
  { key: 'housing', label: 'Housing' },
  { key: 'food', label: 'Food & Groceries' },
  { key: 'transportation', label: 'Transportation' },
  { key: 'healthcare', label: 'Healthcare' },
  { key: 'utilities', label: 'Utilities' },
  { key: 'entertainment', label: 'Entertainment' },
];

// Weights used for the monthly budget estimate (share of after-tax income)
const CATEGORY_WEIGHTS: Record<Category, number> = {
  housing: 0.33,
  food: 0.13,
  transportation: 0.10,
  healthcare: 0.08,
  utilities: 0.06,
  entertainment: 0.05,
};

// ---------------------------------------------------------------------------
// City cost-of-living index data (national average = 100)
// Based on publicly available composite indices for US cities.
// ---------------------------------------------------------------------------

const CITY_DATA: Record<string, CityData> = {
  'New York': { overall: 187, housing: 282, food: 118, transportation: 130, healthcare: 115, utilities: 122, entertainment: 135 },
  'San Francisco': { overall: 179, housing: 272, food: 115, transportation: 128, healthcare: 118, utilities: 109, entertainment: 132 },
  'Los Angeles': { overall: 166, housing: 243, food: 110, transportation: 133, healthcare: 112, utilities: 103, entertainment: 125 },
  'Boston': { overall: 162, housing: 233, food: 111, transportation: 118, healthcare: 122, utilities: 132, entertainment: 120 },
  'Washington DC': { overall: 158, housing: 228, food: 109, transportation: 119, healthcare: 110, utilities: 108, entertainment: 124 },
  'San Diego': { overall: 160, housing: 237, food: 108, transportation: 120, healthcare: 113, utilities: 107, entertainment: 119 },
  'Seattle': { overall: 156, housing: 224, food: 112, transportation: 125, healthcare: 116, utilities: 96, entertainment: 121 },
  'Miami': { overall: 149, housing: 210, food: 107, transportation: 112, healthcare: 108, utilities: 105, entertainment: 118 },
  'Denver': { overall: 139, housing: 181, food: 104, transportation: 107, healthcare: 112, utilities: 96, entertainment: 115 },
  'Portland': { overall: 138, housing: 178, food: 107, transportation: 118, healthcare: 114, utilities: 93, entertainment: 113 },
  'Austin': { overall: 132, housing: 163, food: 98, transportation: 104, healthcare: 105, utilities: 101, entertainment: 114 },
  'Chicago': { overall: 131, housing: 159, food: 104, transportation: 117, healthcare: 110, utilities: 104, entertainment: 112 },
  'Philadelphia': { overall: 129, housing: 153, food: 108, transportation: 116, healthcare: 112, utilities: 113, entertainment: 108 },
  'Nashville': { overall: 121, housing: 141, food: 98, transportation: 99, healthcare: 104, utilities: 95, entertainment: 109 },
  'Minneapolis': { overall: 119, housing: 136, food: 103, transportation: 108, healthcare: 110, utilities: 98, entertainment: 106 },
  'Raleigh': { overall: 112, housing: 126, food: 99, transportation: 97, healthcare: 102, utilities: 96, entertainment: 103 },
  'Atlanta': { overall: 115, housing: 131, food: 101, transportation: 107, healthcare: 106, utilities: 98, entertainment: 105 },
  'Dallas': { overall: 113, housing: 122, food: 98, transportation: 102, healthcare: 105, utilities: 105, entertainment: 107 },
  'Charlotte': { overall: 110, housing: 119, food: 99, transportation: 97, healthcare: 103, utilities: 96, entertainment: 102 },
  'Tampa': { overall: 111, housing: 121, food: 102, transportation: 101, healthcare: 100, utilities: 103, entertainment: 104 },
  'Salt Lake City': { overall: 113, housing: 129, food: 98, transportation: 103, healthcare: 101, utilities: 89, entertainment: 105 },
  'Phoenix': { overall: 109, housing: 118, food: 100, transportation: 102, healthcare: 103, utilities: 102, entertainment: 101 },
  'Houston': { overall: 107, housing: 110, food: 96, transportation: 104, healthcare: 102, utilities: 105, entertainment: 103 },
  'Columbus': { overall: 102, housing: 100, food: 99, transportation: 99, healthcare: 101, utilities: 97, entertainment: 99 },
  'Pittsburgh': { overall: 101, housing: 98, food: 101, transportation: 105, healthcare: 104, utilities: 105, entertainment: 97 },
  'San Antonio': { overall: 99, housing: 93, food: 94, transportation: 98, healthcare: 100, utilities: 101, entertainment: 99 },
  'Indianapolis': { overall: 96, housing: 87, food: 97, transportation: 99, healthcare: 101, utilities: 96, entertainment: 97 },
  'Kansas City': { overall: 97, housing: 89, food: 96, transportation: 101, healthcare: 103, utilities: 101, entertainment: 96 },
  'St. Louis': { overall: 94, housing: 82, food: 98, transportation: 101, healthcare: 102, utilities: 99, entertainment: 95 },
  'Milwaukee': { overall: 97, housing: 91, food: 99, transportation: 103, healthcare: 106, utilities: 100, entertainment: 94 },
  'Detroit': { overall: 93, housing: 78, food: 96, transportation: 107, healthcare: 103, utilities: 103, entertainment: 92 },
  'Memphis': { overall: 89, housing: 72, food: 95, transportation: 96, healthcare: 99, utilities: 96, entertainment: 91 },
  'Oklahoma City': { overall: 90, housing: 76, food: 94, transportation: 97, healthcare: 100, utilities: 98, entertainment: 93 },
};

const CITY_NAMES = Object.keys(CITY_DATA).sort();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function pctDiff(from: number, to: number): number {
  return ((to - from) / from) * 100;
}

function diffLabel(diff: number): string {
  const abs = Math.abs(diff).toFixed(1);
  if (diff > 0) return `${abs}% higher`;
  if (diff < 0) return `${abs}% lower`;
  return 'the same';
}

function diffColor(diff: number): string {
  if (diff > 5) return 'text-red-600';
  if (diff < -5) return 'text-green-600';
  return 'text-gray-700';
}

// Convert an index value to a 0-100 scale for the ProgressBar.
// Indices roughly range from ~70 to ~285; we map that into 0-100.
function indexToBar(value: number): number {
  const min = 60;
  const max = 300;
  return Math.round(Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100)));
}

// ---------------------------------------------------------------------------
// Results interface
// ---------------------------------------------------------------------------

interface ComparisonResults {
  overallDiff: number;
  equivalentSalary: number;
  categoryDiffs: { key: Category; label: string; from: number; to: number; diff: number }[];
  monthlyCurrent: Record<Category, number>;
  monthlyTarget: Record<Category, number>;
  monthlySavings: number;
  biggestDiff: { label: string; diff: number };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CostOfLivingContent() {
  const [currentCity, setCurrentCity] = useState('');
  const [targetCity, setTargetCity] = useState('');
  const [salary, setSalary] = useState(85000);
  const [results, setResults] = useState<ComparisonResults | null>(null);
  const [error, setError] = useState('');

  function compare() {
    setError('');
    setResults(null);

    if (!currentCity || !targetCity) {
      setError('Please select both a current city and a target city.');
      return;
    }
    if (currentCity === targetCity) {
      setError('Please select two different cities to compare.');
      return;
    }
    if (!salary || salary <= 0) {
      setError('Please enter a valid salary.');
      return;
    }

    const from = CITY_DATA[currentCity];
    const to = CITY_DATA[targetCity];

    const overallDiff = pctDiff(from.overall, to.overall);
    const equivalentSalary = salary * (to.overall / from.overall);

    const categoryDiffs = CATEGORIES.map(({ key, label }) => ({
      key,
      label,
      from: from[key],
      to: to[key],
      diff: pctDiff(from[key], to[key]),
    }));

    // Monthly budget estimates (assume ~75% of gross is take-home)
    const monthlyTakeHome = (salary * 0.75) / 12;
    const monthlyCurrent: Record<string, number> = {};
    const monthlyTarget: Record<string, number> = {};
    let totalCurrent = 0;
    let totalTarget = 0;

    for (const cat of CATEGORIES) {
      const base = monthlyTakeHome * CATEGORY_WEIGHTS[cat.key];
      const currentCost = base * (from[cat.key] / 100);
      const targetCost = base * (to[cat.key] / 100);
      monthlyCurrent[cat.key] = currentCost;
      monthlyTarget[cat.key] = targetCost;
      totalCurrent += currentCost;
      totalTarget += targetCost;
    }

    const monthlySavings = totalCurrent - totalTarget;

    // Find the category with the largest absolute difference
    const sorted = [...categoryDiffs].sort(
      (a, b) => Math.abs(b.diff) - Math.abs(a.diff),
    );
    const biggestDiff = { label: sorted[0].label, diff: sorted[0].diff };

    setResults({
      overallDiff,
      equivalentSalary,
      categoryDiffs,
      monthlyCurrent: monthlyCurrent as Record<Category, number>,
      monthlyTarget: monthlyTarget as Record<Category, number>,
      monthlySavings,
      biggestDiff,
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Cost of Living Calculator"
        description="Compare the cost of living between two US cities and find out the equivalent salary you need to maintain your lifestyle after relocating."
        category="Career Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* ---------------------------------------------------------------- */}
        {/* Input Form                                                        */}
        {/* ---------------------------------------------------------------- */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Compare Two Cities
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current City */}
            <div>
              <label
                htmlFor="currentCity"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Current City
              </label>
              <select
                id="currentCity"
                value={currentCity}
                onChange={(e) => setCurrentCity(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
              >
                <option value="">Select a city...</option>
                {CITY_NAMES.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            {/* Target City */}
            <div>
              <label
                htmlFor="targetCity"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Target City
              </label>
              <select
                id="targetCity"
                value={targetCity}
                onChange={(e) => setTargetCity(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
              >
                <option value="">Select a city...</option>
                {CITY_NAMES.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            {/* Current Salary */}
            <div className="md:col-span-2">
              <label
                htmlFor="salary"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Current Annual Salary
              </label>
              <div className="relative max-w-sm">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  $
                </span>
                <input
                  id="salary"
                  type="number"
                  min={0}
                  value={salary}
                  onChange={(e) => setSalary(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-xl pl-8 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <p className="mt-4 text-red-600 text-sm font-medium">{error}</p>
          )}

          {/* Compare button */}
          <div className="mt-8">
            <button
              onClick={compare}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg cursor-pointer"
            >
              Compare Cost of Living
            </button>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Results                                                           */}
        {/* ---------------------------------------------------------------- */}
        {results && (
          <div className="mt-10 space-y-8">
            {/* Overall comparison hero card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Overall Comparison
              </h2>
              <p className="text-gray-600 mb-6">
                {targetCity} is{' '}
                <span className={`font-bold ${diffColor(results.overallDiff)}`}>
                  {diffLabel(results.overallDiff)}
                </span>{' '}
                than {currentCity}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* Current COL index */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    {currentCity}
                  </p>
                  <p className="text-3xl font-extrabold text-gray-900">
                    {CITY_DATA[currentCity].overall}
                  </p>
                  <p className="text-xs text-gray-400">COL Index</p>
                </div>

                {/* Target COL index */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    {targetCity}
                  </p>
                  <p className="text-3xl font-extrabold text-gray-900">
                    {CITY_DATA[targetCity].overall}
                  </p>
                  <p className="text-xs text-gray-400">COL Index</p>
                </div>

                {/* Equivalent Salary — highlighted */}
                <div className="bg-blue-50 rounded-xl border-2 border-blue-500 p-5 relative">
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Equivalent Salary
                  </span>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    You need in {targetCity}
                  </p>
                  <p className="text-3xl font-extrabold text-blue-700">
                    {formatCurrency(results.equivalentSalary)}
                  </p>
                  <p className="text-xs text-gray-400">
                    to match {formatCurrency(salary)}
                  </p>
                </div>
              </div>
            </div>

            {/* Category-by-category breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Category Breakdown
              </h3>

              <div className="space-y-6">
                {results.categoryDiffs.map((cat) => (
                  <div key={cat.key}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-800">
                        {cat.label}
                      </span>
                      <span
                        className={`text-sm font-bold ${diffColor(cat.diff)}`}
                      >
                        {diffLabel(cat.diff)}
                      </span>
                    </div>

                    {/* Side-by-side bars */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <ProgressBar
                          value={indexToBar(cat.from)}
                          label={currentCity}
                          showValue={false}
                        />
                        <p className="text-xs text-gray-500 mt-1 text-right">
                          Index: {cat.from}
                        </p>
                      </div>
                      <div>
                        <ProgressBar
                          value={indexToBar(cat.to)}
                          label={targetCity}
                          showValue={false}
                        />
                        <p className="text-xs text-gray-500 mt-1 text-right">
                          Index: {cat.to}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly budget comparison */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Estimated Monthly Budget Comparison
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Based on your {formatCurrency(salary)} salary (estimated 75%
                take-home rate)
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 pr-4 font-semibold text-gray-600">
                        Category
                      </th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-600">
                        {currentCity}
                      </th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-600">
                        {targetCity}
                      </th>
                      <th className="text-right py-3 pl-4 font-semibold text-gray-600">
                        Difference
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {CATEGORIES.map(({ key, label }) => {
                      const cur = results.monthlyCurrent[key];
                      const tar = results.monthlyTarget[key];
                      const diff = tar - cur;
                      return (
                        <tr
                          key={key}
                          className="border-b border-gray-100 last:border-0"
                        >
                          <td className="py-3 pr-4 font-medium text-gray-800">
                            {label}
                          </td>
                          <td className="py-3 px-4 text-right text-gray-700 font-mono">
                            {formatCurrency(cur)}
                          </td>
                          <td className="py-3 px-4 text-right text-gray-700 font-mono">
                            {formatCurrency(tar)}
                          </td>
                          <td
                            className={`py-3 pl-4 text-right font-mono font-semibold ${
                              diff > 0
                                ? 'text-red-600'
                                : diff < 0
                                  ? 'text-green-600'
                                  : 'text-gray-600'
                            }`}
                          >
                            {diff > 0 ? '+' : ''}
                            {formatCurrency(diff)}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Total row */}
                    <tr className="border-t-2 border-gray-300">
                      <td className="py-3 pr-4 font-bold text-gray-900">
                        Total Estimated
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-gray-900">
                        {formatCurrency(
                          Object.values(results.monthlyCurrent).reduce(
                            (s, v) => s + v,
                            0,
                          ),
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-gray-900">
                        {formatCurrency(
                          Object.values(results.monthlyTarget).reduce(
                            (s, v) => s + v,
                            0,
                          ),
                        )}
                      </td>
                      <td
                        className={`py-3 pl-4 text-right font-mono font-bold ${
                          results.monthlySavings > 0
                            ? 'text-green-600'
                            : results.monthlySavings < 0
                              ? 'text-red-600'
                              : 'text-gray-600'
                        }`}
                      >
                        {results.monthlySavings > 0 ? '-' : '+'}
                        {formatCurrency(Math.abs(results.monthlySavings))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Key differences */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Biggest difference */}
              <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
                <h4 className="text-sm uppercase tracking-wide text-amber-700 font-semibold mb-2">
                  Biggest Difference
                </h4>
                <p className="text-lg font-bold text-gray-900">
                  {results.biggestDiff.label}
                </p>
                <p
                  className={`text-sm font-semibold ${diffColor(results.biggestDiff.diff)}`}
                >
                  {diffLabel(results.biggestDiff.diff)} in {targetCity}
                </p>
              </div>

              {/* Monthly impact */}
              <div
                className={`rounded-2xl border p-6 ${
                  results.monthlySavings >= 0
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <h4
                  className={`text-sm uppercase tracking-wide font-semibold mb-2 ${
                    results.monthlySavings >= 0
                      ? 'text-green-700'
                      : 'text-red-700'
                  }`}
                >
                  Monthly Impact
                </h4>
                <p className="text-lg font-bold text-gray-900">
                  {results.monthlySavings >= 0
                    ? `Save ${formatCurrency(results.monthlySavings)}/mo`
                    : `Spend ${formatCurrency(Math.abs(results.monthlySavings))}/mo more`}
                </p>
                <p className="text-sm text-gray-600">
                  {results.monthlySavings >= 0
                    ? `That's ${formatCurrency(results.monthlySavings * 12)} per year in savings`
                    : `That's ${formatCurrency(Math.abs(results.monthlySavings) * 12)} more per year`}
                </p>
              </div>
            </div>

            {/* Salary equivalence breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                How We Calculated the Equivalent Salary
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>Your Current Salary</span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(salary)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>{currentCity} COL Index</span>
                  <span className="font-mono font-semibold">
                    {CITY_DATA[currentCity].overall}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>{targetCity} COL Index</span>
                  <span className="font-mono font-semibold">
                    {CITY_DATA[targetCity].overall}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>Ratio ({targetCity} / {currentCity})</span>
                  <span className="font-mono font-semibold">
                    {(CITY_DATA[targetCity].overall / CITY_DATA[currentCity].overall).toFixed(3)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span>Equivalent Salary in {targetCity}</span>
                  <span className="font-mono font-bold text-blue-700">
                    {formatCurrency(salary)} x{' '}
                    {(CITY_DATA[targetCity].overall / CITY_DATA[currentCity].overall).toFixed(3)}{' '}
                    = {formatCurrency(results.equivalentSalary)}
                  </span>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 text-xs text-gray-500 leading-relaxed">
              <strong>Disclaimer:</strong> These cost of living indices are
              approximations based on publicly available data and are intended
              for informational purposes only. Actual costs vary based on
              individual lifestyle, neighborhood, family size, and spending
              habits. This tool does not account for state/local tax
              differences. Always do additional research before making
              relocation decisions.
            </div>

            {/* Educational Content */}
            <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Understanding Cost of Living: What Goes Into the Numbers</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Cost of living measures how much money you need to cover basic expenses -- housing, food, transportation,
                  healthcare, utilities, and everyday goods -- in a given location. These indices are calculated by comparing
                  the price of a standardized basket of goods and services across cities, with the national average typically
                  set at 100. A city with an index of 150 means expenses are roughly 50% higher than average.
                </p>
                <p>
                  <strong>Housing</strong> is by far the largest driver of cost-of-living differences between cities,
                  often accounting for 30-40% of household spending. In high-cost metros like San Francisco and New York,
                  housing indices can exceed 250, while affordable cities in the Midwest and South may sit below 90.
                  The Bureau of Labor Statistics tracks price changes over time through the Consumer Price Index (CPI),
                  which measures inflation across categories such as shelter, food, energy, and medical care.
                </p>
                <p>
                  When evaluating a relocation or remote-work salary adjustment, it is important to consider more than just
                  the overall index. State and local income taxes, property taxes, and sales taxes vary significantly and are
                  not always captured in a single COL number. Similarly, lifestyle factors -- commute length, childcare costs,
                  and access to public transit -- can shift your real budget in ways a broad index does not reflect.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.bls.gov/cpi/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics Consumer Price Index (CPI)</a>
                  {' · '}
                  <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">BLS Occupational Employment &amp; Wage Statistics by Area</a>
                  {' · '}
                  <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop &ndash; U.S. Department of Labor Salary &amp; Cost-of-Living Tools</a>
                </p>
              </div>
            </section>

            {/* CTA */}
            <ToolCTA
              headline="Relocating? Update your resume first."
              description="Whether you're chasing a higher salary or a lower cost of living, a polished resume helps you land the right offer. Build yours in minutes."
              buttonText="Build My Resume Free"
            />
          </div>
        )}
      </div>
    </div>
  );
}
